import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkOutline20 from '@carbon/icons-react/es/checkmark--outline/20';
import Information20 from '@carbon/icons-react/es/information/20';
import Link16 from '@carbon/icons-react/es/link/16';
import Unlink16 from '@carbon/icons-react/es/unlink/16';
import { Divider, Space, Tooltip, Typography } from 'antd';
import React, { ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import { SalesforceConfig, SalesforceIntegrationId } from 'api/integrations';
import { Button } from 'components/atoms/button';
import { InlineNotification } from 'components/atoms/inline-notification';
import { InlineNotificationType } from 'components/atoms/inline-notification/inline-notification-types';
import { Modal } from 'components/atoms/modal';
import { useTranslation } from 'hooks/use-translation';
import {
  useDisconnectSalesForceMutation,
  useGetSfInstallationsQuery,
  useSalesForceAuthenticateQuery,
} from 'queries/salesforce-integration';
import { IntegrationStatusTypes } from '../../authentication-types';
import { AlertButton, Message } from '../../styles';
import { SalesforceLayout } from '../salesforce-layout';

const { Text } = Typography;

const SubText = styled.div`
  color: var(--gray-100);
  margin: 10px 0px;
  font-size: 12px;
  margin-left: var(--spacing-07);
`;

const Container = styled.div`
  padding: 60px;
`;

const StyledSettingsTitle = styled.div`
  font-size: 1rem;
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-03);
`;

const InstalledSuccessfullyText = styled.p`
  color: var(--green-50);
  margin-left: var(--spacing-08);
`;

const CheckMarkWrapper = styled.span<{ isHidden?: boolean }>`
  margin-right: var(--spacing-05);
  margin-top: var(--spacing-03);
  visibility: ${(props) => (props.isHidden ? 'hidden' : 'visible')};
  svg {
    fill: var(--green-50);
  }
`;

const StyledStrong = styled.strong`
  margin-right: var(--spacing-02);
`;

const StyledButton = styled(Button)`
  margin-left: var(--spacing-07);
`;

const StyledText = styled(Text)`
  margin-left: var(--spacing-07);
`;

const CheckMark = ({ isCompleted }: { isCompleted?: boolean }) =>
  isCompleted ? (
    <CheckMarkWrapper>
      <CheckmarkOutline20 />
    </CheckMarkWrapper>
  ) : (
    <CheckMarkWrapper isHidden>
      <CheckmarkOutline20 />
    </CheckMarkWrapper>
  );

const SalesforceSettingLayout = ({
  btnText,
  icon,
  subtext,
  href,
  disabled,
  loading,
  heading,
  isTextOnly,
  isStepCompleted,
}: {
  btnText: ReactElement;
  icon: ReactElement;
  subtext: ReactElement;
  href: string | undefined;
  disabled: boolean;
  loading: boolean;
  heading?: ReactElement;
  isTextOnly?: boolean;
  isStepCompleted?: boolean;
}) => (
  <div>
    <StyledSettingsTitle>
      <CheckMark isCompleted={isStepCompleted} />
      {heading}
    </StyledSettingsTitle>
    {isTextOnly ? (
      btnText
    ) : (
      <StyledButton
        ghost
        type="primary"
        icon={icon}
        iconPlacement="right"
        href={href}
        disabled={disabled}
        loading={loading}
      >
        {btnText}
      </StyledButton>
    )}
    <SubText>{subtext}</SubText>
  </div>
);

export const SalesforceSettings = ({ data }: { data?: SalesforceConfig }) => {
  const { integrationId } = useParams<SalesforceIntegrationId>();
  const history = useHistory();

  const { mutate, isLoading } = useDisconnectSalesForceMutation();
  const { isLoading: isLoadingAuth, data: authDetails } =
    useSalesForceAuthenticateQuery({});

  const { data: installationData } = useGetSfInstallationsQuery(
    Number(integrationId),
    integrationId !== 'new',
  );
  const [showDisconnectButton, setShowDisconnectButton] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const onMouserHandle = (status = false) => {
    setShowDisconnectButton(status);
  };

  const handleDisconnect = () => {
    setShowDisconnectModal(true);
  };

  const onDisconnect = async () => {
    if (integrationId !== 'new') {
      await mutate(Number(integrationId), {
        onSuccess: () => {
          history.push('/settings/integrations/');
        },
      });
    }
  };
  const alertMessage = useTranslation('integration.alert.error.message');
  const reAuthMessage = useTranslation(
    'settings.integrations.sf.settings.alert.button.message',
  );
  const reAuthenticate = useTranslation(
    'settings.integration.alert.button.message',
  );
  const isStepCompleted = integrationId === 'new';

  return (
    <SalesforceLayout integrationStatus={data?.integrationStatus}>
      <Container>
        {integrationId !== 'new' ? (
          <>
            {data?.integrationStatus === IntegrationStatusTypes.Expired && (
              <InlineNotification
                msg={
                  <Message>
                    <strong>{alertMessage}</strong> {reAuthMessage}
                  </Message>
                }
                type={InlineNotificationType.Error}
                button={
                  <AlertButton
                    size="small"
                    type="tertiary"
                    href={authDetails?.oauthReauthUrl}
                  >
                    {reAuthenticate}
                  </AlertButton>
                }
                icon={<Information20 />}
              />
            )}
            <Space direction="vertical" align="start">
              <StyledSettingsTitle>
                <CheckMark isCompleted={isStepCompleted} />
                <FormattedMessage
                  id={
                    !authDetails?.marketplaceAppInstallationRequired
                      ? 'settings.integrations.sf.settings.title_without_step'
                      : 'settings.integrations.sf.settings.title'
                  }
                  values={{
                    strong: (chunks) => <StyledStrong>{chunks}</StyledStrong>,
                  }}
                />
              </StyledSettingsTitle>
              <Tooltip
                title={<FormattedMessage id="sf.connected.message" />}
                visible={showDisconnectButton}
                placement="bottom"
              >
                <StyledButton
                  type="primary"
                  icon={showDisconnectButton ? <Unlink16 /> : <Link16 />}
                  iconPlacement="right"
                  onClick={handleDisconnect}
                  onMouseEnter={() => onMouserHandle(true)}
                  onMouseLeave={() => onMouserHandle(false)}
                  danger={!!showDisconnectButton}
                >
                  {data?.meta.salesforceEnv === 'SANDBOX' ? (
                    showDisconnectButton ? (
                      <FormattedMessage id="disconnect" />
                    ) : (
                      <FormattedMessage id="settings.integrations.sf.settings.connected.sandbox" />
                    )
                  ) : showDisconnectButton ? (
                    <FormattedMessage id="disconnect" />
                  ) : (
                    <FormattedMessage id="settings.integrations.sf.settings.connected.production" />
                  )}
                </StyledButton>
              </Tooltip>
              {data?.meta.oauthAuthenticatorEmail ? (
                <>
                  <StyledText>
                    {data?.meta.oauthAuthenticatorAccountUrl ? (
                      <FormattedMessage
                        id="settings.integrations.sf.settings.connected.to.as"
                        values={{
                          connector: (
                            <b>{data?.meta.oauthAuthenticatorEmail}</b>
                          ),
                          company: (
                            <b>
                              {
                                data?.meta.oauthAuthenticatorAccountUrl?.split(
                                  'https://',
                                )[1]
                              }
                            </b>
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="settings.integrations.sf.settings.connected.as"
                        values={{
                          connector: (
                            <b>{data?.meta.oauthAuthenticatorEmail}</b>
                          ),
                        }}
                      />
                    )}
                  </StyledText>
                </>
              ) : null}
              <>
                {authDetails?.marketplaceAppInstallationRequired === true ? (
                  installationData !== undefined &&
                  installationData?.length > 0 ? (
                    <>
                      <Divider />
                      <SalesforceSettingLayout
                        icon={<ArrowRight16 />}
                        href={authDetails?.marketplaceAppUrl}
                        disabled={integrationId === 'new'}
                        heading={
                          <FormattedMessage
                            id="settings.integrations.sf.settings.install_app_title"
                            values={{
                              strong: (chunks) => (
                                <StyledStrong>{chunks}</StyledStrong>
                              ),
                            }}
                          />
                        }
                        btnText={
                          <InstalledSuccessfullyText>
                            <FormattedMessage id="sf.app.installed_successfully" />
                          </InstalledSuccessfullyText>
                        }
                        subtext={<></>}
                        loading={isLoadingAuth}
                        isTextOnly
                        isStepCompleted
                      />
                    </>
                  ) : (
                    <>
                      <Divider />
                      <SalesforceSettingLayout
                        icon={<ArrowRight16 />}
                        href={authDetails?.marketplaceAppUrl}
                        disabled={integrationId === 'new'}
                        heading={
                          <FormattedMessage
                            id="settings.integrations.sf.settings.install_app_title"
                            values={{
                              strong: (chunks) => (
                                <StyledStrong>{chunks}</StyledStrong>
                              ),
                            }}
                          />
                        }
                        btnText={
                          <FormattedMessage id="settings.integrations.sf.settings.install_rocketlane_in_sf" />
                        }
                        subtext={<></>}
                        loading={isLoadingAuth}
                      />
                    </>
                  )
                ) : null}
              </>
            </Space>

            <Modal
              visible={showDisconnectModal}
              title={
                <FormattedMessage id="settings.integrations.sf.disconnect.title" />
              }
              confirmLoading={false}
              onCancel={() => setShowDisconnectModal(false)}
              onOk={onDisconnect}
              okText={<FormattedMessage id="disconnect" />}
              okType="danger"
              okButtonProps={{ loading: isLoading }}
              cancelButtonProps={{ disabled: isLoading }}
              closable={!isLoading}
            >
              <p>
                <FormattedMessage id="settings.integrations.sf.disconnect.content" />
              </p>
            </Modal>
          </>
        ) : (
          <>
            <SalesforceSettingLayout
              heading={
                <FormattedMessage
                  id={
                    !authDetails?.marketplaceAppInstallationRequired
                      ? 'settings.integrations.sf.settings.title_without_step'
                      : 'settings.integrations.sf.settings.title'
                  }
                  values={{
                    strong: (chunks) => <StyledStrong>{chunks}</StyledStrong>,
                  }}
                />
              }
              icon={<ArrowRight16 />}
              href={authDetails?.oauthUrl}
              disabled={false}
              btnText={
                <FormattedMessage id="settings.integrations.sf.settings.connect.production" />
              }
              subtext={
                <FormattedMessage
                  id="settings.integrations.sf.settings.connect.production.subtext"
                  values={{
                    a: (chunks) => (
                      <a href={authDetails?.oauthSandboxUrl}>{chunks}</a>
                    ),
                  }}
                />
              }
              loading={isLoadingAuth}
            />
            {authDetails?.marketplaceAppInstallationRequired === true ? (
              <>
                <Divider />
                <SalesforceSettingLayout
                  icon={<ArrowRight16 />}
                  href={authDetails?.marketplaceAppUrl}
                  disabled={integrationId === 'new'}
                  heading={
                    <FormattedMessage
                      id="settings.integrations.sf.settings.install_app_title"
                      values={{
                        strong: (chunks) => (
                          <StyledStrong>{chunks}</StyledStrong>
                        ),
                      }}
                    />
                  }
                  btnText={
                    <FormattedMessage id="settings.integrations.sf.settings.install_rocketlane_in_sf" />
                  }
                  subtext={<></>}
                  loading={isLoadingAuth}
                />
              </>
            ) : null}
          </>
        )}
      </Container>
    </SalesforceLayout>
  );
};
